import { useState, useEffect } from 'react';
import { Star, Trash2, Check, X, Loader2, MessageSquare, RefreshCw } from 'lucide-react';
import axios_instance from '../api/axios_instance';

const TABS = [
  { key: 'pending',  label: 'قيد الانتظار' },
  { key: 'approved', label: 'معتمدة' },
];

const StarDisplay = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map(s => (
      <Star
        key={s}
        className={`w-3.5 h-3.5 ${s <= rating ? 'fill-amber-400 text-amber-400' : 'fill-gray-200 text-gray-200'}`}
      />
    ))}
  </div>
);

export default function ManageComments() {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null); // comment id being acted on

  const fetchComments = async () => {
    setLoading(true);
    try {
      const res = await axios_instance.get('/api/comments');
      const data = res.data.comments ?? res.data ?? [];
      setComments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchComments(); }, []);

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      const res = await axios_instance.patch(`/api/comments/${id}/approve`);
      setComments(prev => prev.map(c => c._id === id ? { ...c, isApproved: res.data.isApproved } : c));
    } catch (err) {
      console.error('Approve error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id) => {
    setActionLoading(id);
    try {
      const res = await axios_instance.patch(`/api/comments/${id}/approve`);
      setComments(prev => prev.map(c => c._id === id ? { ...c, isApproved: res.data.isApproved } : c));
    } catch (err) {
      console.error('Reject error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('حذف هذا التعليق نهائياً؟')) return;
    setActionLoading(id);
    try {
      await axios_instance.delete(`/api/comments/${id}`);
      setComments(prev => prev.filter(c => c._id !== id));
    } catch (err) {
      console.error('Delete error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = comments.filter(c => {
    if (activeTab === 'approved') return c.isApproved === true;
    if (activeTab === 'pending') return c.isApproved === false;
    return false;
  });

  const countFor = (key) => comments.filter(c => {
    if (key === 'approved') return c.isApproved === true;
    if (key === 'pending') return c.isApproved === false;
    return false;
  }).length;

  const formatDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString('ar-TN', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-black text-gray-900">إدارة التعليقات</h2>
          <p className="text-sm font-bold text-gray-400 mt-1">{comments.length} تعليق إجمالاً</p>
        </div>
        <button
          onClick={fetchComments}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-gray-400 text-gray-600 font-black text-sm hover:border-emerald-600 hover:text-emerald-600 transition-all"
        >
          <RefreshCw size={15} />
          تحديث
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl font-black text-sm border-2 transition-all ${
              activeTab === tab.key
                ? 'bg-emerald-600 border-emerald-600 text-white'
                : 'border-gray-200 text-gray-500 hover:border-gray-400'
            }`}
          >
            {tab.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-black ${
              activeTab === tab.key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
            }`}>
              {countFor(tab.key)}
            </span>
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center border-2 border-dashed border-gray-200 rounded-3xl">
          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
            <MessageSquare className="w-7 h-7 text-gray-300" />
          </div>
          <p className="font-black text-gray-400">لا توجد تعليقات في هذه الفئة</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(comment => {
            const isActing = actionLoading === comment._id;
            const productName = comment.product?.name?.ar || comment.product?.name?.en || comment.productId || '—';

            return (
              <div
                key={comment._id}
                className="bg-white border-2 border-gray-100 rounded-3xl p-5 flex flex-col sm:flex-row gap-4"
              >
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-black text-base flex-shrink-0">
                  {(comment.authorName || '?').charAt(0).toUpperCase()}
                </div>

                {/* Body */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-3 mb-1">
                    <span className="font-black text-gray-900 text-sm">{comment.authorName}</span>
                    <StarDisplay rating={comment.rating} />
                    <span className="text-[11px] font-bold text-gray-400">{formatDate(comment.createdAt)}</span>
                  </div>

                  {/* Product tag */}
                  <p className="text-[11px] font-bold text-emerald-600 mb-2">
                    📦 {productName}
                  </p>

                  <p className="text-sm text-gray-700 font-bold leading-relaxed">{comment.content}</p>
                </div>

                {/* Actions */}
                <div className="flex sm:flex-col gap-2 shrink-0">
                  {activeTab === 'pending' && (
                    <button
                      onClick={() => handleApprove(comment._id)}
                      disabled={isActing}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-50 text-emerald-600 font-black text-xs border-2 border-emerald-200 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all disabled:opacity-50"
                    >
                      {isActing ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                      قبول
                    </button>
                  )}
                  {activeTab === 'approved' && (
                    <button
                      onClick={() => handleReject(comment._id)}
                      disabled={isActing}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-50 text-gray-500 font-black text-xs border-2 border-gray-200 hover:bg-gray-200 transition-all disabled:opacity-50"
                    >
                      {isActing ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
                      إلغاء القبول
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(comment._id)}
                    disabled={isActing}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-50 text-red-400 font-black text-xs border-2 border-red-100 hover:bg-red-500 hover:text-white hover:border-red-500 transition-all disabled:opacity-50"
                  >
                    {isActing ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                    حذف
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
